import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortal } from '@/contexts/PortalContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  ArrowLeft,
  FileText, 
  User, 
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronRight,
  Loader2,
  Award,
  Send,
  Eye,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  submitted: { label: 'Submitted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  late: { label: 'Late', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  graded: { label: 'Graded', color: 'bg-purple-100 text-purple-700', icon: Award },
  returned: { label: 'Returned', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

export default function InstructorSubmissions() {
  const { token } = useAuth();
  const { isInstructor } = usePortal();
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get('assignment');
  
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [grading, setGrading] = useState(false);
  const [gradingData, setGradingData] = useState({
    score: '',
    feedback: '',
    rubric_scores: {},
    is_published: false
  });

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!assignmentId) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/portal/assignments/${assignmentId}/submissions`,
          {
            credentials: 'include',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setAssignment({
            assignment_id: data.assignment_id,
            title: data.assignment_title,
            points_possible: data.points_possible,
            rubric: data.rubric
          });
          setSubmissions(data.submissions || []);
        }
      } catch (err) {
        console.error('Failed to fetch submissions:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isInstructor) {
      fetchSubmissions();
    } else {
      setLoading(false);
    }
  }, [token, assignmentId, isInstructor]);

  const openGradingModal = async (submission) => {
    try {
      const response = await fetch(
        `${API_URL}/api/portal/assignments/submissions/${submission.submission_id}`,
        {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSelectedSubmission(data);
        
        // Pre-fill with existing grade if any
        if (data.grade) {
          setGradingData({
            score: data.grade.score?.toString() || '',
            feedback: data.grade.feedback || '',
            rubric_scores: data.grade.rubric_scores || {},
            is_published: data.grade.is_published || false
          });
        } else {
          setGradingData({
            score: '',
            feedback: '',
            rubric_scores: {},
            is_published: false
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch submission:', err);
      toast.error('Failed to load submission details');
    }
  };

  const handleGradeSubmit = async (publish = false) => {
    if (!gradingData.score || isNaN(parseFloat(gradingData.score))) {
      toast.error('Please enter a valid score');
      return;
    }

    setGrading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/portal/assignments/submissions/${selectedSubmission.submission.submission_id}/grade`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            score: parseFloat(gradingData.score),
            feedback: gradingData.feedback || null,
            rubric_scores: Object.keys(gradingData.rubric_scores).length > 0 
              ? gradingData.rubric_scores 
              : null,
            is_published: publish,
            return_for_revision: false
          })
        }
      );

      if (response.ok) {
        toast.success(publish ? 'Grade published!' : 'Grade saved as draft');
        setSelectedSubmission(null);
        
        // Refresh submissions
        const refreshResponse = await fetch(
          `${API_URL}/api/portal/assignments/${assignmentId}/submissions`,
          {
            credentials: 'include',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }
        );
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          setSubmissions(data.submissions || []);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save grade');
      }
    } catch (err) {
      toast.error('Failed to save grade');
    } finally {
      setGrading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-gray-500">Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (!isInstructor) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-2">Instructor Access Required</h3>
          <p className="text-gray-500">This page is only accessible to instructors.</p>
        </CardContent>
      </Card>
    );
  }

  if (!assignmentId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">View Submissions</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Select an Assignment</h3>
            <p className="text-gray-500 mb-4">
              Go to the assignments page and click on an assignment to view its submissions.
            </p>
            <Link to="/portal/instructor/assignments">
              <Button className="bg-[#1E3A8A]">View Assignments</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const needsGrading = submissions.filter(s => !s.grade?.score).length;
  const graded = submissions.filter(s => s.grade?.score).length;

  return (
    <div className="space-y-6" data-testid="instructor-submissions-page">
      {/* Back Button */}
      <Link to="/portal/instructor/assignments" className="inline-flex items-center text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Assignments
      </Link>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">{assignment?.title}</h1>
        <div className="flex items-center gap-6 text-blue-100">
          <span>{assignment?.points_possible} points</span>
          <span>{submissions.length} submissions</span>
          <span>{needsGrading} need grading</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{submissions.length}</div>
            <div className="text-sm text-gray-500">Total Submissions</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">{needsGrading}</div>
            <div className="text-sm text-gray-500">Needs Grading</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{graded}</div>
            <div className="text-sm text-gray-500">Graded</div>
          </CardContent>
        </Card>
      </div>

      {/* Submissions List */}
      {submissions.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">No Submissions Yet</h3>
            <p className="text-gray-500">Students haven't submitted their work yet.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Student Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {submissions.map((submission) => {
                const config = statusConfig[submission.status] || statusConfig.submitted;
                const StatusIcon = config.icon;
                
                return (
                  <div 
                    key={submission.submission_id}
                    className="py-4 flex items-center justify-between hover:bg-gray-50 -mx-4 px-4 cursor-pointer"
                    onClick={() => openGradingModal(submission)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{submission.student_name}</h4>
                        <p className="text-sm text-gray-500">{submission.student_email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Submitted</div>
                        <div className="text-sm font-medium">{formatDate(submission.submitted_at)}</div>
                      </div>
                      
                      {submission.grade?.score !== null && submission.grade?.score !== undefined ? (
                        <div className="text-right min-w-[80px]">
                          <div className="text-lg font-bold text-purple-600">
                            {submission.grade.score}/{assignment?.points_possible}
                          </div>
                          <Badge className={submission.grade.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                            {submission.grade.is_published ? 'Published' : 'Draft'}
                          </Badge>
                        </div>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-700">
                          <Clock className="w-3 h-3 mr-1" />
                          Needs Grading
                        </Badge>
                      )}
                      
                      {submission.is_late && (
                        <Badge className="bg-orange-100 text-orange-700">Late</Badge>
                      )}
                      
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grading Modal */}
      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Grade Submission</DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-6">
              {/* Student Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-[#1E3A8A] rounded-full flex items-center justify-center text-white font-semibold">
                  {selectedSubmission.student.name?.charAt(0) || 'S'}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{selectedSubmission.student.name}</h4>
                  <p className="text-sm text-gray-500">{selectedSubmission.student.email}</p>
                </div>
              </div>

              {/* Submission Content */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Student Response</h4>
                <div className="border rounded-lg p-4 bg-gray-50 max-h-[300px] overflow-y-auto">
                  {selectedSubmission.submission.text_response ? (
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {selectedSubmission.submission.text_response}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-gray-500">No text response provided.</p>
                  )}
                </div>
              </div>

              {/* Rubric Scoring */}
              {selectedSubmission.assignment.rubric && selectedSubmission.assignment.rubric.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Rubric Scoring</h4>
                  <div className="space-y-3">
                    {selectedSubmission.assignment.rubric.map((criterion, idx) => (
                      <div key={idx} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{criterion.name}</span>
                          <span className="text-sm text-gray-500">Max: {criterion.points} pts</span>
                        </div>
                        <Input
                          type="number"
                          min="0"
                          max={criterion.points}
                          placeholder={`Score (0-${criterion.points})`}
                          value={gradingData.rubric_scores[criterion.name] || ''}
                          onChange={(e) => setGradingData({
                            ...gradingData,
                            rubric_scores: {
                              ...gradingData.rubric_scores,
                              [criterion.name]: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="w-32"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Score & Feedback */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Score (out of {selectedSubmission.assignment.points_possible})
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max={selectedSubmission.assignment.points_possible}
                    value={gradingData.score}
                    onChange={(e) => setGradingData({...gradingData, score: e.target.value})}
                    placeholder="Enter score"
                  />
                </div>
                <div className="flex items-end">
                  {gradingData.score && (
                    <div className="text-lg font-bold text-purple-600">
                      {((parseFloat(gradingData.score) / selectedSubmission.assignment.points_possible) * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Feedback to Student
                </label>
                <Textarea
                  value={gradingData.feedback}
                  onChange={(e) => setGradingData({...gradingData, feedback: e.target.value})}
                  placeholder="Provide feedback (supports Markdown)..."
                  className="min-h-[150px]"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setSelectedSubmission(null)}>
                  Cancel
                </Button>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => handleGradeSubmit(false)}
                    disabled={grading}
                  >
                    {grading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Draft'}
                  </Button>
                  <Button 
                    className="bg-[#1E3A8A]"
                    onClick={() => handleGradeSubmit(true)}
                    disabled={grading}
                  >
                    {grading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Publish Grade
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
